/**
 * Modal — overlay centralizado, fecha em ESC ou clique fora.
 */

import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import clsx from "clsx";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  /** Renderiza esses elementos no rodapé. */
  footer?: ReactNode;
}

const SIZES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = "md",
  footer,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm animate-fade-in"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={clsx(
          // O painel é uma coluna com teto de altura: cabeçalho e rodapé
          // fixos, corpo rolando. Sem isso, um modal alto (uma ocorrência com
          // ligações, linha do tempo e comentários) crescia além da tela e o
          // botão de fechar ficava fora do alcance — sem rolagem nenhuma,
          // porque o `overflow-hidden` daqui cortava o excedente.
          "flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-xl bg-white dark:bg-slate-900 shadow-card-hover dark:shadow-none dark:ring-1 dark:ring-slate-800",
          SIZES[size],
        )}
      >
        {(title || description) && (
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-surface-100 dark:border-slate-800 px-5 py-4">
            <div>
              {title && (
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
              )}
              {description && (
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-slate-400 hover:bg-surface-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-surface-100 dark:border-slate-800 bg-surface-50 dark:bg-slate-800/40 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
